/**
 * Iter 34d — Opportunity Mode UI label source of truth.
 *
 * Single shared lookup for Analyse / Fix / Fit screens to decide:
 *   • which mode the API returned (with safe fallbacks),
 *   • the visible mode-pill label,
 *   • the page hero / score / CTA / fit / fix titles,
 *   • the score number suffix.
 *
 * This is intentionally a tiny module (no React, no styling). The
 * three result screens import what they need; consultancy mode is
 * the default so any unfamiliar / missing payload falls back to the
 * pre-Iter-34 labels — i.e. existing tenants see no change.
 */

export const MODES = {
  CONSULTANCY: 'consultancy',
  PURCHASE: 'purchase_readiness',
  SUPPLY: 'supply_readiness',
};

const _LABELS = {
  consultancy: {
    pill: null, // never render a pill in consultancy mode
    analyseTitle: 'Delivery Analysis',
    analyseScoreLabel: 'Overall Risk Score',
    analyseCta: 'Generate Remediation Roadmap',
    analyseCtaLoading: 'Generating Roadmap...',
    fixTitle: 'Remediation Roadmap',
    fitTitle: 'Commercial Fit Assessment',
    fitHero: 'Engagement Fit',
    generateAnalyseCta: 'Run Analysis',
    generateFitCta: 'Generate Fit Assessment',
  },
  purchase_readiness: {
    pill: 'Purchase readiness',
    analyseTitle: 'Purchase Readiness Analysis',
    analyseScoreLabel: 'Purchase Readiness',
    analyseCta: 'Generate Opportunity Plan',
    analyseCtaLoading: 'Generating Plan...',
    fixTitle: 'Opportunity Plan',
    fitTitle: 'Purchase Fit',
    fitHero: 'Approach Shape',
    generateAnalyseCta: 'Analyse Purchase Readiness',
    generateFitCta: 'Assess Purchase Fit',
  },
  supply_readiness: {
    pill: 'Supply readiness',
    analyseTitle: 'Supply Readiness Analysis',
    analyseScoreLabel: 'Supply Readiness',
    analyseCta: 'Generate Supply Opportunity Plan',
    analyseCtaLoading: 'Generating Plan...',
    fixTitle: 'Supply Opportunity Plan',
    fitTitle: 'Supply Fit',
    fitHero: 'Approach Shape',
    generateAnalyseCta: 'Analyse Supply Readiness',
    generateFitCta: 'Assess Supply Fit',
  },
};

/**
 * Resolve the opportunity mode for any Analyse / Fix / Fit payload.
 * Reads (in order):
 *   1. payload.opportunity_mode
 *   2. payload.metadata?.opportunity_mode
 *   3. payload.mode
 *   4. fallback → consultancy
 */
export function resolveModeFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return MODES.CONSULTANCY;
  const m = payload.opportunity_mode
    || (payload.metadata && payload.metadata.opportunity_mode)
    || payload.mode
    || MODES.CONSULTANCY;
  if (m === MODES.PURCHASE || m === MODES.SUPPLY || m === MODES.CONSULTANCY) {
    return m;
  }
  return MODES.CONSULTANCY;
}

/**
 * Return the full label set for a resolved mode. Unknown modes get
 * the consultancy bundle so the UI never crashes.
 */
export function labelsForMode(mode) {
  return _LABELS[mode] || _LABELS[MODES.CONSULTANCY];
}

/**
 * Return the mode-pill label (or null when the operator should not
 * see one — i.e. consultancy default).
 */
export function modePillLabel(mode) {
  return labelsForMode(mode).pill;
}

/**
 * Convenience — read the mode-named additive payload block off an
 * Analyse response (`purchase_readiness` / `supply_readiness`).
 * Returns null when absent.
 */
export function modeBlockFromAnalysis(analysis) {
  if (!analysis || typeof analysis !== 'object') return null;
  const mode = resolveModeFromPayload(analysis);
  if (mode === MODES.CONSULTANCY) return null;
  return analysis[mode] || null;
}
